import React, { useCallback, useEffect, useState } from 'react';
import { Check, MessageSquare, X } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';
import { Card, btnGhost, btnPrimary, btnSmall, errorText, inputCls } from '../finance/financeUi';

const label = (v) => (v ? String(v).replace(/_/g, ' ') : '—');

function Row({ t, onDone }) {
  const [mode, setMode] = useState('');
  const [text, setText] = useState('');
  const act = async (path, body, message) => {
    try {
      await api.request(`/onboarding/admin/${path}/${t.tenant_id}`, { method: 'POST', body: JSON.stringify(body || {}) });
      toast.success(message);
      onDone();
    } catch (err) { toast.error(errorText(err)); }
  };
  return (
    <li className="px-5 py-4" data-testid={`pending-tenant-${t.tenant_id}`}>
      <div className="grid gap-3 md:grid-cols-[1.4fr_1.4fr_1fr_auto] md:items-center">
        <div className="min-w-0">
          <p className="truncate font-medium">{t.company_name}</p>
          <p className="text-xs text-muted-foreground">{label(t.business_type)} · requested {label(t.requested_account_type).toUpperCase()}</p>
        </div>
        <div className="min-w-0 text-sm">
          <p className="truncate">{t.owner_name || '—'}</p>
          <p className="truncate text-xs text-muted-foreground">{t.owner_email} · {t.owner_phone || '—'}</p>
        </div>
        <div className="text-xs text-muted-foreground">
          <p>{t.team_size ? `${t.team_size} expected users` : 'Team size not given'}</p>
          <p>Signed up {t.created_at ? new Date(t.created_at).toLocaleDateString() : '—'}</p>
          {t.info_requested && <p className="text-amber-700" data-testid={`info-requested-${t.tenant_id}`}>Info requested</p>}
        </div>
        <div className="flex flex-wrap gap-2">
          <button type="button" className={btnPrimary} onClick={() => setMode(mode === 'approve' ? '' : 'approve')} data-testid={`approve-tenant-${t.tenant_id}`}>
            <Check className="h-4 w-4" />Approve
          </button>
          <button type="button" className={btnSmall} onClick={() => setMode('info')} data-testid={`request-info-${t.tenant_id}`}>
            <MessageSquare className="h-3.5 w-3.5" />More info
          </button>
          <button type="button" className={btnSmall} onClick={() => setMode('reject')} data-testid={`reject-tenant-${t.tenant_id}`}>
            <X className="h-3.5 w-3.5" />Reject
          </button>
        </div>
      </div>
      {mode === 'approve' && (
        <div className="mt-3 flex flex-wrap items-center gap-2 rounded-lg bg-primary/5 p-3 text-sm" data-testid="approve-tenant-confirm">
          Approve {t.company_name}? The owner can sign in, and economics are configured next in Team Management.
          <button type="button" className={btnPrimary} onClick={() => act('approve-iso', {}, `${t.company_name} approved`)} data-testid="approve-tenant-confirm-btn">Yes, approve</button>
          <button type="button" className={btnGhost} onClick={() => setMode('')}>Cancel</button>
        </div>
      )}
      {(mode === 'info' || mode === 'reject') && (
        <div className="mt-3 flex flex-wrap gap-2">
          <input className={`${inputCls} md:w-96`} value={text} onChange={(e) => setText(e.target.value)}
                 placeholder={mode === 'info' ? 'What do you need from them?' : 'Reason (optional)'} data-testid="tenant-action-text" />
          <button type="button" className={btnPrimary} disabled={mode === 'info' && !text.trim()} data-testid="tenant-action-confirm"
                  onClick={() => (mode === 'info'
                    ? act('request-info', { message: text }, 'Information requested')
                    : act('reject-iso', { reason: text }, 'Signup rejected'))}>
            {mode === 'info' ? 'Send request' : 'Reject signup'}
          </button>
          <button type="button" className={btnGhost} onClick={() => setMode('')}>Cancel</button>
        </div>
      )}
    </li>
  );
}

export function PendingApprovalsPanel({ onChanged }) {
  const [rows, setRows] = useState(null);
  const load = useCallback(() => {
    api.request('/onboarding/admin/pending-isos').then((d) => setRows(d.pending_isos || [])).catch(() => setRows([]));
  }, []);
  useEffect(() => { load(); }, [load]);
  return (
    <Card testid="pending-approvals-panel" title="Pending approval"
          aside={<span className="text-xs text-muted-foreground">{rows?.length ?? 0} request(s)</span>}>
      <ul className="divide-y">
        {(rows || []).map((t) => <Row key={t.tenant_id} t={t} onDone={() => { load(); onChanged?.(); }} />)}
        {rows && !rows.length && <li className="px-5 py-10 text-center text-sm text-muted-foreground" data-testid="no-pending-tenants">No signup requests waiting.</li>}
      </ul>
    </Card>
  );
}
